import {
  Component,
  Inject,
  Input,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChildren,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import Swal from 'sweetalert2';
import { WINDOW } from '../constants/window';
import { LoaderService } from '../loader/loader.service';
import {
  ConditionalLogic,
  FormDataPayload,
  IDropdownData,
  Label,
  PreviewData,
  PreviewQuestionAnswerDetailsSaved,
  QuestionDetails,
  RenderPreview,
} from './buyer-preview.models';
import { BuyerPreviewService } from './buyer-preview.service';
import { HandleAnswerService } from './handle-answer.service';
import { ScrollItemIntoViewDirective } from './scroll-item-into-view.directive';
import { DoesHiddenInputExistPipe } from './does-hidden-input-exist.pipe';
import { GetValidationIconPipe } from './get-validation-icon.pipe';
import { EvalScriptPipe } from '../shared/eval-script.pipe';
import { SafeHtmlPipe } from '../services/safe-html.pipe';
import { environment } from '../../environments/environment';
import { SubmitSurveyConfirmationComponent } from './submit-survey-confirmation/submit-survey-confirmation.component';
import { ScoresAnalysisComponent } from './scores-analysis/scores-analysis.component';
import { FormsModule } from '@angular/forms';
import { ListenClickDirective } from './listen-click.directive';
import { NgClass, JsonPipe } from '@angular/common';

@Component({
    selector: 'app-buyer-preview',
    templateUrl: './buyer-preview.component.html',
    styleUrls: ['./buyer-preview.component.css'],
    standalone: true,
    imports: [
    NgClass,
    ScrollItemIntoViewDirective,
    ListenClickDirective,
    FormsModule,
    ScoresAnalysisComponent,
    SubmitSurveyConfirmationComponent,
    JsonPipe,
    SafeHtmlPipe,
    EvalScriptPipe,
    GetValidationIconPipe,
    DoesHiddenInputExistPipe
],
})
export class BuyerPreviewComponent implements OnDestroy {
  labels: Label[] = new Array(new Label());
  toggleJsonStructure = false;

  currentPreviewTriggeredFromQuestion: RenderPreview = new RenderPreview();
  currentlyPreviewedQuestion: RenderPreview = new RenderPreview();

  @Input() isMgmtPreview: boolean = false;
  @Input() previewModeFlag: boolean = false;
  @Input() mode: string = '';
  @Input() renderPreviewData: RenderPreview[] = new Array(new RenderPreview());  
  surveyRefID = localStorage.getItem('SurveyId')

  @ViewChildren(ScrollItemIntoViewDirective)
  scrollableItems: QueryList<ScrollItemIntoViewDirective>;

  enableNavigation: boolean = false;
  dataToPreview: PreviewData = new PreviewData();
  renderSubmitterPreview: boolean = false;

  public loaderService = inject(LoaderService);

  disablePreviousButton: boolean = false;
  disableNextButton: boolean = false;
  doesHiddenInputExistInTheCurrPreview: boolean = false;
  enableSubmitButton: boolean = false;
  isSubmitterSectionAnswered: boolean = false;
  areAnswersMappedIntoUIBySetAction: boolean = false;

  doesValidSubmitterDataExist: boolean = false;
  doesValidNonSubmitterDataExist: boolean = false;
  doesConditionalLogicExist: boolean = false;

  isViewReady: boolean = false;

  CONDITIONAL_LOGIC_JSON: ConditionalLogic[] = [];

  sanitizer = inject(DomSanitizer);
  private _buyerPreviewService = inject(BuyerPreviewService);
  private _handleAnswerService = inject(HandleAnswerService);
  placeholderList: any[] = [];
  private _placeholderSub?: Subscription;

  constructor(@Inject(WINDOW) private _window: Window) {
    this.labels = this._buyerPreviewService.labels;
  }

  private _templateId = '';
  public get templateId() {
    let localStorageTemplateId = localStorage.getItem('TemplateId');
    return (this._templateId = localStorageTemplateId ?? '');
  }

  private _formTitle = '';
  public get formTitle() {
    let localStorageFormTitle = localStorage.getItem('Title');
    return (this._formTitle = localStorageFormTitle ?? '');
  }

  private _surveyId = '';
  public get surveyId() {
    let localStorageSurveyId = localStorage.getItem('SurveyId');
    return (this._surveyId = localStorageSurveyId ?? '');
  }

  private _formLayout = '';
  public get formLayout() {
    let localStorageFormLayout = localStorage.getItem('FormLayout');
    return (this._formLayout = localStorageFormLayout ?? '');
  }

  private _formType = '';
  public get formType() {
    let localStorageFormType = localStorage.getItem('FormType');
    return (this._formType = localStorageFormType ?? '');
  }

  private _orgFormId = '';
  public get orgFormId() {
    let localStorageOrgFormId = localStorage.getItem('OrgFormId');
    return (this._orgFormId = localStorageOrgFormId ?? '');
  }

  private _formId = 0;
  public get formId() {
    let localStorageFormId = localStorage.getItem('FormId');
    return (this._formId = Number(JSON.parse(localStorageFormId)) ?? 0);
  }

  private _submitFormRedirectURL = '';
  public get submitFormRedirectURL() {
    // Use environment's homePageUrl to build redirect URL for Supplier form details page
    // Format: {homePageUrl}/form-management/supplier-form-details/{OrgFormRefId}
    const homePageUrl = environment.homePageUrl?.replace(/\/$/, '') || '';
    if (homePageUrl && this.orgFormId) {
      return `${homePageUrl}/form-management/supplier-form-details/${this.orgFormId}`;
    }
    // Fallback to localStorage value if environment not configured
    let localStorageSubmitFormRedirectURL = localStorage.getItem(
      'SubmitFormRedirectURL'
    );
    return (this._submitFormRedirectURL =
      localStorageSubmitFormRedirectURL ?? '');
  }

  private _isParentShared = false;
  public get isParentShared() {
    let localStorageIsParentShared = localStorage.getItem('IsParentShared');
    return (this._isParentShared =
      localStorageIsParentShared?.toLowerCase() === 'true' ? true : false);
  }

  questionsList: QuestionDetails[] = [];
  cqJsonWithIdsReplaced: ConditionalLogic[] = [];

  swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: 'btn',
    },
    buttonsStyling: false,
  });

  replacePlaceholdersInText(fieldText: string) {
    this.placeholderList.forEach(placeholder => {
      const placeholderTag = `{{# ${placeholder.key} #}}`;
      fieldText = fieldText?.replaceAll(placeholderTag, placeholder.value);
    });
    return fieldText;
  }

  replaceIdsWithNames(originalCqJSON: ConditionalLogic[]) {
    let originalCqJSONCopy = [...originalCqJSON];
    return (originalCqJSONCopy = [...originalCqJSON].map(
      (logicObj: ConditionalLogic) => {
        let modifiedLogicObj = { ...logicObj };
        modifiedLogicObj.id = this.getQnName(logicObj.id);
        if (logicObj.next && logicObj.next.length !== 0) {
          modifiedLogicObj.next = { ...logicObj }.next.map((nextNodeId) =>
            this.getQnName(nextNodeId)
          );
        }

        if (logicObj.navigate && logicObj.navigate.length !== 0) {
          modifiedLogicObj.navigate = logicObj.navigate.map((conditionItem) => {
            let modifiedConditionItem = { ...conditionItem };

            if (conditionItem.next && conditionItem.next.length !== 0) {
              modifiedConditionItem.next = { ...conditionItem }.next.map(
                (nextNodeId) => this.getQnName(nextNodeId)
              );
            }

            if (
              conditionItem.mandatoryNodes &&
              conditionItem.mandatoryNodes.length !== 0
            ) {
              modifiedConditionItem.mandatoryNodes = {
                ...conditionItem,
              }.mandatoryNodes.map((mandatoryNode) =>
                this.getQnName(mandatoryNode)
              );
            }

            return modifiedConditionItem;
          });
        }

        return modifiedLogicObj;
      }
    ));
  }
  getQnName(qnId: string) {
    const name =
      qnId === ''
        ? qnId
        : this.questionsList?.find((qnObj) => qnObj?.questionId === qnId)
            ?.questionName;
    return name;
  }

  mapSavedAnswersIntoSupplierPreviewUI(savedField: FormDataPayload) {
    const currQues = this.renderPreviewData?.find(
      (qnObj) => savedField.id === qnObj.data[0].QuestionId
    );
    if (currQues) {
      if (currQues?.data[0]?.type?.toLowerCase() !== 'submitter') {
        if (this.doesDisableInputExistFor(currQues)) {
          this.autoUpdateAnswerOnDisabledInput(currQues);
          if (currQues.data[0].QuestionId === savedField.id) {
            savedField.answers = currQues.nodeLogic.selectedAnswers;
          }
        }
        this.preview(currQues);
        currQues.nodeLogic.selectedAnswers = new Array(0);
        currQues.nodeLogic.selectedAnswers.push.apply(
          currQues.nodeLogic.selectedAnswers,
          savedField.answers
        );
        currQues.answered =
          !this.isArrayOfEmptyStrings(currQues.nodeLogic.selectedAnswers) &&
          currQues.nodeLogic.selectedAnswers.length !== 0;
        if (currQues.answered) {
          this._handleAnswerService.setAnswersFor(currQues, 'setAction');
        }
      } else if (currQues?.data[0]?.type.toLowerCase() === 'submitter') {
        if (currQues?.data[0]?.QuestionId === savedField?.id) {
          currQues.nodeLogic.selectedAnswers = savedField?.answers;
          // SM-1679: Save/clear submitter value in localStorage for Tab navigation sync
          if (savedField?.answers?.length > 0 && savedField.answers[0]) {
            localStorage.setItem(savedField.id, savedField.answers[0]);
          } else {
            // Clear localStorage when answer is empty to keep icons in sync
            localStorage.removeItem(savedField.id);
          }
          currQues.answered =
            currQues?.nodeLogic?.selectedAnswers?.length !== 0 &&
            !this.isArrayOfEmptyStrings(currQues?.nodeLogic?.selectedAnswers);
          this.isSubmitterSectionAnswered =
            this.checkIfSubmitterSectionIsAnswered();
        }
      }
    }
  }

downloadFormQuestionAttachment(id: any, fileName: any){
this._buyerPreviewService.downloadFormQuestionAttachment(id, fileName, this.surveyRefID).subscribe((blob: any) => {
  this._buyerPreviewService.saveFile(blob, fileName);
});
}

removeFormQuestionAttachment(id: any, fileName: any){
    this.currentlyPreviewedQuestion.nodeLogic.selectedAnswers = []
    this.currentlyPreviewedQuestion.answered = false
  }

  getAlreadySavedAnswersIntoSupplierPreview() {
    if (this.mode === 'supplierEdit' || this.mode === 'previewDisabled') {
      this._buyerPreviewService.getUserAnswers(this.surveyId).subscribe({
        next: (resp) => {
          if (resp?.Data) {
            const savedAnswersApiResp = [...resp.Data].map((ansObj) => ({
              id: ansObj.Key, 
              answers: [...ansObj.Value],
            }));
            savedAnswersApiResp.forEach((savedField: FormDataPayload) =>
              this.mapSavedAnswersIntoSupplierPreviewUI(savedField)
            );
          }
          this.preview(
            this.renderPreviewData.find(
              (item) => item.nodeLogic.previous[0].toUpperCase() === 'START'
            )
          );
        },
        error: () => {},
      });
    }
  }

  getConditionalLogicJSON() {
    this._buyerPreviewService
      .getConditionalLogicJSON(this.templateId)
      .subscribe({
        next: (resp) => {
          this.doesConditionalLogicExist = Boolean(resp?.Data?.length !== 0);
          this.CONDITIONAL_LOGIC_JSON = resp.Data.map((arrObj, idx) => {
            const firstQuestionId = this.renderPreviewData?.find(
              (qnObj) => qnObj.qcount == 1
            )?.data[0]?.QuestionId;
            if (firstQuestionId && arrObj.id === firstQuestionId)
              return { ...arrObj, selectedAnswers: [], previous: ['START'] };
            else return { ...arrObj, selectedAnswers: [], previous: [''] };
          });

          this.appendRequiredKeysToTheRenderPreviewDataArr();

          this.updateAnswersForDisabledInputsOfRenderPreviewData();

          this.getRenderPreviewDataFromLocalStorage();

          // For supplier preview before the nodes are rendered on UI, map the answers that are already saved by the user
          this.getAlreadySavedAnswersIntoSupplierPreview();

          this.makeNodesRenderOnListItemsUI(
            this.CONDITIONAL_LOGIC_JSON.filter(
              (cqItem) => cqItem.mandatory
            ).map((item) => item.id)
          );

          const startItem = this.renderPreviewData.find(
            (item) => item.nodeLogic.previous[0].toUpperCase() === 'START'
          );
          if (startItem) {
            this.preview(startItem);
          }

          this.printIdsReplacedCQJSON();
        },
        error: (err) => {

          const swalWithBootstrapButtons = Swal.mixin({
            customClass: {
              confirmButton: 'btn',
            },
            buttonsStyling: false,
          });
          swalWithBootstrapButtons.fire({
            title: 'Failure!',
            text:
              !err.IsSuccess || err.Message != ''
                ? err.Message
                : 'Cannot fetch conditional logic.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        },
      });
  }

  probePreviousButton() {
    const doesPreviousNodeExist = Boolean(this.inquirePreviousNode());
    return (this._buyerPreviewService.disablePreviousButton =
      !doesPreviousNodeExist);
  }
  probeNextButton() {
    const doesNextNodeExist = Boolean(this.inquireNextNode());

    //If there exists no next node - then the curr preview is the last question
    if (this.currentlyPreviewedQuestion) {
      this.currentlyPreviewedQuestion.isLastNonSubmitterItem =
        this._buyerPreviewService.isLastNonSubmitterQnPreview =
          !doesNextNodeExist;
    }

    return (this._buyerPreviewService.disableNextButton =
      !doesNextNodeExist ||
      ((this.currentlyPreviewedQuestion?.nodeLogic?.mandatory ||
        this.currentlyPreviewedQuestion?.shallRenderOnListItemsUI) &&
        !this.currentlyPreviewedQuestion?.answered));
  }
  probeNavigationButtons() {
    this.disablePreviousButton = this.probePreviousButton();
    this.disableNextButton = this.probeNextButton();
  }

  getRespectiveListItemScrolledIntoView(qnObj: RenderPreview) {
    if (this.scrollableItems) {
      const item = this.scrollableItems.find(
        (scrollableListItem) =>
          scrollableListItem.itemId === qnObj.data[0].QuestionId
      );
      if (item) {
        item.getScrolledIntoView();
      }
    }
  }

  autoUpdateAnswerOnDisabledInput(qnObj: RenderPreview) {
    if (qnObj?.data[1]?.isDisableInputChecked) {
      if (qnObj?.data[1]?.type?.toLowerCase() === 'dropdown') {
        qnObj.answered = true;
        qnObj.data[1].dropdowndata[0].selected = true;
        qnObj.nodeLogic.selectedAnswers = [
          qnObj?.data[1]?.dropdowndata?.at(0)?.answer ?? '',
        ];
      }
    }
  }

  preview(qnObj: RenderPreview) {
    this.getRespectiveListItemScrolledIntoView(qnObj);

    this.renderSubmitterPreview =
      this._buyerPreviewService.renderSubmitterPreview = false;

    this.currentlyPreviewedQuestion = qnObj;

    this.disableNavigationForStaticNodes();
    this.probeNavigationButtons();
  }

  previewSubmitter() {
    this.renderSubmitterPreview =
      this._buyerPreviewService.renderSubmitterPreview = true;

    this.enableNavigation = true;
  }

  appendRequiredKeysToTheRenderPreviewDataArr() {
    this.renderPreviewData = this.renderPreviewData.map((item) => {
      return {
        ...item,
        shallRenderOnListItemsUI:
          this.currentlyPreviewedQuestion.shallRenderOnListItemsUI ?? false,
        nodeLogic:
          this.CONDITIONAL_LOGIC_JSON.find(
            (cqItem) => cqItem.id === item.data[0].QuestionId.trim()
          ) ?? new ConditionalLogic(),
        answered: this.currentlyPreviewedQuestion.answered ?? null,
        valid: (this.currentlyPreviewedQuestion.valid ?? null) || ((item?.data[1]?.TypeValue === "Attachment" && this.currentlyPreviewedQuestion.answered) ?? true),
        isLastNonSubmitterItem:
          this.currentlyPreviewedQuestion.isLastNonSubmitterItem ?? false,
      };
    });
  }

  updateAnswersForDisabledInputsOfRenderPreviewData() {
    const questionsWithDisabledInput = this.renderPreviewData?.filter((qnObj) =>
      this.doesDisableInputExistFor(qnObj)
    );
    questionsWithDisabledInput?.forEach((qnObj) =>
      this.autoUpdateAnswerOnDisabledInput(qnObj)
    );
  }

  makeNodesRenderOnListItemsUI(nodes: string[]) {
    if (nodes && nodes.length !== 0) {
      this.renderPreviewData
        .filter((item) => nodes?.includes(item.data[0].QuestionId.trim()))
        .forEach((item) => (item.shallRenderOnListItemsUI = true));
      this.cookDataToRenderThePreview();
    }
  }

  makeNodesHiddenOnListItemsUIAndClearAnswers(nodes: string[]) {

    if (nodes && nodes.length !== 0) {
      this.renderPreviewData
        .filter((item) => nodes?.includes(item.data[0].QuestionId.trim()))
        .forEach((item) => {
          item.shallRenderOnListItemsUI = false;
          this._handleAnswerService.clearAnswersFor(item);
        });


      this.cookDataToRenderThePreview();
    }
  }

  showRespectiveMandatoryNodesForAllTheQuestionsAnswered() {
    // Display mandatory nodes for only the selected condition For all the questions with answers selected
    this.renderPreviewData.forEach((qn: RenderPreview) => {
      if (qn.answered) {
        if (
          qn.nodeLogic &&
          qn.nodeLogic.navigate &&
          qn.nodeLogic.navigate.length !== 0
        ) {
          const navigationForTheSelectedAnswers = qn.nodeLogic.navigate?.filter(
            (navigationItem) =>
              qn.nodeLogic.selectedAnswers
                ?.map((x) => x?.toLowerCase())
                ?.includes(navigationItem.condition?.toLowerCase())
          );
          navigationForTheSelectedAnswers?.forEach(
            (navigationForTheSelectedAnswer) => {
              if (
                navigationForTheSelectedAnswer &&
                navigationForTheSelectedAnswer.mandatoryNodes
              ) {
                this.makeNodesRenderOnListItemsUI(
                  navigationForTheSelectedAnswer.mandatoryNodes
                );
              }
            }
          );
        }
      }
    });
  }

  cookDataToRenderThePreview() {
    this.dataToPreview = {
      submitterData: this.renderPreviewData.filter(
        (item) => item.data[0].type.toLowerCase() === 'submitter'
      ),
      nonSubmitterData: this.renderPreviewData.filter(
        (item) =>
          item.data[0].type.toLowerCase() !== 'submitter' &&
          item.shallRenderOnListItemsUI
      ),
    };
    this.doesValidSubmitterDataExist = Boolean(
      this.dataToPreview?.submitterData?.length !== 0 &&
        this.dataToPreview?.submitterData?.every(
          (qnObj) => qnObj?.data[0]?.QuestionId !== ''
        )
    );
    this.doesValidNonSubmitterDataExist = Boolean(
      this.dataToPreview?.nonSubmitterData?.length !== 0 &&
        this.dataToPreview?.nonSubmitterData?.every(
          (qnObj) => qnObj?.data[0]?.QuestionId !== ''
        )
    );
  }

  modifyCurrPreviewQnInRenderPreviewData() {
    this.renderPreviewData = this.renderPreviewData.map((item) => {
      if (
        item.data[0].QuestionId.trim() ===
        this.currentlyPreviewedQuestion.data[0].QuestionId.trim()
      ) {
        return this.currentlyPreviewedQuestion;
      } else return item;
    });
  }

  saveLocallyRenderPreviewData() {
    localStorage.setItem(
      'RENDER_PREVIEW_QUESTIONS_DATA',
      JSON.stringify(this.renderPreviewData)
    );
  }

  modifyAndSaveLocallyRenderPreviewData() {
    this.modifyCurrPreviewQnInRenderPreviewData();
    this.saveLocallyRenderPreviewData();
  }

  ngAfterViewInit() {
    this._handleAnswerService.mode = this.mode ?? '';
    this._buyerPreviewService.isMgmtPreview = this.isMgmtPreview;
    this.isViewReady = this._buyerPreviewService.isViewReady = true;
    this.disableNavigationForStaticNodes();
  }

  printIdsReplacedCQJSON() {
    // replace the IDS in the JSON with names (just for debugging  not for consumption in logic)
    // Only needed for debugging. Not needed for PROD
    let cqLogic = [...this.CONDITIONAL_LOGIC_JSON];
    let allQuestionsData = [...this.renderPreviewData];
    if (allQuestionsData) {
      this.questionsList = [...allQuestionsData].map((qnObj) => ({
        questionId: qnObj.data[0].QuestionId,
        questionName: qnObj.data[0].dispalyFCText,
      }));
      this.cqJsonWithIdsReplaced = this.replaceIdsWithNames(cqLogic);
    }
  }

  doesHiddenInputExistFor(qnObj: RenderPreview) {
    return qnObj?.data?.some((fieldItem) => fieldItem?.isHidden);
  }

  doesDisableInputExistFor(qnObj: RenderPreview) {
    return qnObj?.data?.some((fieldItem) => fieldItem?.isDisableInputChecked);
  }

  disableNavigationForStaticNodes() {
    this.doesHiddenInputExistInTheCurrPreview = this.doesHiddenInputExistFor(
      this.currentlyPreviewedQuestion
    );

    if (
      this.isViewReady &&
      this.currentlyPreviewedQuestion &&
      this.doesHiddenInputExistInTheCurrPreview
    ) {
      this.enableNavigation = this._buyerPreviewService.enableNavigation =
        false;
    } else {
      this.enableNavigation = this._buyerPreviewService.enableNavigation = true;
    }
  }

  ngOnInit() {
    this._handleAnswerService.mode = this.mode ?? '';
    this.getConditionalLogicJSON();
    // Subscribe to reactive placeholder list to ensure placeholders are resolved
    // even when HTTP response arrives after component initialization (SM-3309)
    this._placeholderSub = this._buyerPreviewService.placeholderList$.subscribe(list => {
      this.placeholderList = list;
    });
  }

  syncRenderPreviewData() {
    this._buyerPreviewService.renderPreviewDataQue$.subscribe({
      next: (qnObjArr: RenderPreview[]) => {
        this.renderPreviewData = qnObjArr;
      },
    });
  }

  getRenderPreviewDataFromLocalStorage() {
    const LOCAL_RENDER_PREVIEW_QUESTIONS_DATA = JSON.parse(
      localStorage.getItem('RENDER_PREVIEW_QUESTIONS_DATA')
    );
    if (LOCAL_RENDER_PREVIEW_QUESTIONS_DATA) {
      this.renderPreviewData = LOCAL_RENDER_PREVIEW_QUESTIONS_DATA;
    }
  }

  checkIfSubmitterSectionIsAnswered() {
    return this.renderPreviewData
      ?.filter((qnObj) => qnObj?.data[0]?.type?.toLowerCase() === 'submitter')
      ?.every((qnObj) => qnObj?.answered);
  }

  ngOnChanges(changes: SimpleChanges) {

    this.syncRenderPreviewData();

    this._buyerPreviewService.helper$.asObservable().subscribe({
      next: (helperVal: string) => {
        if (
          helperVal === 'clicked on preview question input element' ||
          helperVal === 'set on preview question input element'
        ) {
          this.areAnswersMappedIntoUIBySetAction = Boolean(
            helperVal === 'set on preview question input element'
          );
          this.updateCurrentlyPreviewedQuestionAnswerFromLocal();
          this.modifyAndSaveLocallyRenderPreviewData();

          this.probeNavigationButtons();

        } else if (helperVal === 'SUBMIT_SURVEY_OK') {
          this.submit();
          console.log("Buyer preview submit successful");
        } else if (helperVal.includes('submitter__')) {
          const questionId = helperVal?.replace('submitter__textboxID_', '');
          const currSubmitterQn = this.renderPreviewData?.find(
            (qnObj) => qnObj?.data[0]?.QuestionId === questionId
          );
          // SM-1679: Read current value from localStorage to sync selectedAnswers
          // This fixes validation icons not updating correctly when using Tab navigation
          const currentValue = localStorage.getItem(questionId);
          currSubmitterQn.nodeLogic.selectedAnswers = currentValue ? [currentValue] : [];
          
          currSubmitterQn.answered =
            currSubmitterQn?.nodeLogic?.selectedAnswers?.length !== 0 &&
            !this.isArrayOfEmptyStrings(
              currSubmitterQn?.nodeLogic?.selectedAnswers
            );
          currSubmitterQn.valid = currSubmitterQn?.answered;
          this.isSubmitterSectionAnswered =
            this.checkIfSubmitterSectionIsAnswered();
        }
      },
    });

    this._buyerPreviewService.logicExecutionStr.subscribe({
      next: (logicStr: string) => {
        if (logicStr === 'goToNextNode') {
          this.goToNextNode(this.currentlyPreviewedQuestion);
        } else if (logicStr === 'goToPreviousNode') {
          this.goToPreviousNode();
        } else if (logicStr === 'getScores') {
          this.getScores();
        }
      },
    });
  }

  updateCurrentlyPreviewedQuestionAnswerFromLocal() {
    const currentlyPreviewedQuestionAnswer: PreviewQuestionAnswerDetailsSaved =
      JSON.parse(
        localStorage.getItem(this.currentlyPreviewedQuestion.data[0].QuestionId)
      );

    if (currentlyPreviewedQuestionAnswer) {
      // Mark the respective options as selected in JSON
      if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
        'dropdown'
      ) {
        // Reset all options to have selected = false
        this.currentlyPreviewedQuestion?.data[1]?.dropdowndata?.forEach(
          (opt) => (opt.selected = false)
        );

        //set only selected option to have selected = true
        const selectedOption =
          this.currentlyPreviewedQuestion?.data[1]?.dropdowndata?.find(
            (dropdownEle: IDropdownData) =>
              dropdownEle?.optionid?.toString() ===
              currentlyPreviewedQuestionAnswer?.answerUniqueId?.toString()
          );
        if (selectedOption) {
          selectedOption.selected = true;
        }
      } else if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
        'multiselect-dropdown'
      ) {
        // Reset all options to have selected = false
        this.currentlyPreviewedQuestion?.data[1]?.multiselectDropdowndata?.forEach(
          (opt) => (opt.selected = false)
        );

        //set only selected option(s) to have selected = true
        const selectedOptions =
          this.currentlyPreviewedQuestion?.data[1]?.multiselectDropdowndata?.filter(
            (multiselectDropdownEle: IDropdownData) =>
              currentlyPreviewedQuestionAnswer?.answerUniqueId?.includes(
                multiselectDropdownEle.optionid?.toString()
              )
          );
        if (
          selectedOptions.length !== 0 &&
          !this.isArrayOfEmptyStrings(selectedOptions)
        ) {
          selectedOptions
            ?.filter((nonEmptyStrId) => nonEmptyStrId)
            ?.forEach((selectedOption) => (selectedOption.selected = true));
        }
      } else if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
        'checkbox'
      ) {
        // Reset all options to have selected = false
        this.currentlyPreviewedQuestion?.data[1]?.checkboxdata?.forEach(
          (opt) => (opt.selected = false)
        );

        //set only selected option(s) to have selected = true
        const checkedOptions =
          this.currentlyPreviewedQuestion?.data[1]?.checkboxdata?.filter(
            (checkboxEle: IDropdownData) =>
              currentlyPreviewedQuestionAnswer?.answerUniqueId
                ?.map((x) => x?.replace('Checkbox_', ''))
                ?.includes(checkboxEle.optionid?.toString())
          );
        if (
          checkedOptions.length !== 0 &&
          !this.isArrayOfEmptyStrings(checkedOptions)
        ) {
          checkedOptions
            ?.filter((nonEmptyStrId) => nonEmptyStrId)
            ?.forEach((checkedOption) => (checkedOption.selected = true));
        }
      } else if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
        'radio'
      ) {
        // Reset all checkboxes to have selected = false
        this.currentlyPreviewedQuestion?.data[1]?.radiodata?.forEach(
          (opt) => (opt.selected = false)
        );
        //set only selected option to have selected = true
        const selectedRadio =
          this.currentlyPreviewedQuestion?.data[1]?.radiodata?.find(
            (radioEle: IDropdownData) =>
              radioEle?.optionid?.toString() ===
              currentlyPreviewedQuestionAnswer?.answerUniqueId?.toString()
          );
        if (selectedRadio) {
          selectedRadio.selected = true;
        }
      }
      this.currentlyPreviewedQuestion.nodeLogic.selectedAnswers =
        currentlyPreviewedQuestionAnswer.answerValue;

      this.currentlyPreviewedQuestion.answered =
        currentlyPreviewedQuestionAnswer.answerValue
          ? Boolean(currentlyPreviewedQuestionAnswer.answerValue)
          : null;

      this.currentlyPreviewedQuestion.valid =
        currentlyPreviewedQuestionAnswer.isAnswerValid;

      this.currentlyPreviewedQuestion.isAnswerModified =
        currentlyPreviewedQuestionAnswer.isAnswerModified;
    }

    if (this.currentlyPreviewedQuestion.isAnswerModified) {
      if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
          'dropdown' ||
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
          'multiselect-dropdown'
      ) {
        this._handleAnswerService.setDropdownInput(
          this.currentlyPreviewedQuestion,
          'clickAction'
        );
      } else if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
        'checkbox'
      ) {
        this._handleAnswerService.setCheckboxInput(
          this.currentlyPreviewedQuestion,
          'clickAction'
        );
      } else if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
        'radio'
      ) {
        this._handleAnswerService.setRadioInput(
          this.currentlyPreviewedQuestion,
          'clickAction'
        );
      } else if (
        this.currentlyPreviewedQuestion?.data[1]?.type?.toLowerCase() ===
        'textarea'
      ) {
        this._handleAnswerService.setTextAreaInput(
          this.currentlyPreviewedQuestion,
          'clickAction'
        );
      } else {
        this.currentlyPreviewedQuestion.data[1].question.changingThisBreaksApplicationSecurity =
          currentlyPreviewedQuestionAnswer?.modifiedField?.changingThisBreaksApplicationSecurity;
      }
    }

    if (
      this.currentlyPreviewedQuestion.isAnswerModified &&
      this.currentlyPreviewedQuestion.nodeLogic &&
      this.currentlyPreviewedQuestion.nodeLogic.navigate &&
      this.currentlyPreviewedQuestion.nodeLogic.navigate.length !== 0
    ) {
      this.onAnswerUpdate(currentlyPreviewedQuestionAnswer);
    }
    this.syncRenderPreviewData();
  }

  isArrayOfEmptyStrings(arr: string[]) {
    if (!arr) arr = [''];
    return arr.every((str) => str === '');
  }

  doesValidConditionMandatoryNodesInclude(
    answeredQues: RenderPreview,
    qnObj: RenderPreview
  ) {
    let result = [];

    const selectedCondition = answeredQues?.nodeLogic?.navigate?.find(
      (navigationItem) =>
        answeredQues?.nodeLogic?.selectedAnswers?.includes(
          navigationItem?.condition
        )
    );
    const selectedConditionMandatoryNodes =
      selectedCondition?.mandatoryNodes ?? [''];
    result.push(
      selectedConditionMandatoryNodes?.includes(qnObj.data[0].QuestionId)
    );
    return true;
  }

  isMandatoryNodeForTheCurrentSelectionOfTheOtherCombinationNode(
    qnObj: RenderPreview
  ) {

    const allAnsweredQuestionsRenderedOnListItemsUI =
      this.renderPreviewData.filter(
        (qnObj) => qnObj.answered && qnObj.shallRenderOnListItemsUI
      );

    return allAnsweredQuestionsRenderedOnListItemsUI?.every((answeredQues) =>
      this.doesValidConditionMandatoryNodesInclude(answeredQues, qnObj)
    );
  }

  handleSubNodesFor(invalidConditionMandatoryNodesArr: string[]) {
    // Remove any empty strings from the nodes arr
    if (invalidConditionMandatoryNodesArr) {
      const invalidConditionMandatoryNodes =
        invalidConditionMandatoryNodesArr.filter((nodeId) => nodeId !== '');
      if (
        invalidConditionMandatoryNodes &&
        invalidConditionMandatoryNodes.length !== 0 &&
        !this.isArrayOfEmptyStrings(invalidConditionMandatoryNodes)
      ) {
        const invalidConditionMandatoryNodesObjArr =
          invalidConditionMandatoryNodes.map((nodeId) =>
            this.renderPreviewData.find(
              (qnObj) => qnObj.data[0].QuestionId === nodeId
            )
          );

        if (invalidConditionMandatoryNodesObjArr) {
          const invalidConditionMandatoryNodesWithAnswer =
            invalidConditionMandatoryNodesObjArr.filter((qnObj) => {
              return qnObj.answered;
            });
          // if invalidConditionMandatoryNode (3) is answered
          // get the navigation[] for the invalidConditionMandatoryNode
          invalidConditionMandatoryNodesWithAnswer.forEach((nodeWithAnswer) => {
            if (
              nodeWithAnswer.nodeLogic &&
              nodeWithAnswer.nodeLogic.navigate &&
              nodeWithAnswer.nodeLogic.navigate.length !== 0
            ) {
              // get mandatoryNodes for all the naviagationItem conditions and
              // if anymore subnodes are there handle those subnodes & conditions else hidethem
              nodeWithAnswer.nodeLogic.navigate.forEach((navigationItem) => {
                if (
                  navigationItem.mandatoryNodes &&
                  navigationItem.mandatoryNodes.length !== 0 &&
                  !this.isArrayOfEmptyStrings(navigationItem.mandatoryNodes)
                ) {
                  this.handleSubNodesFor(navigationItem.mandatoryNodes);
                } else {
                  // hide them
                  this.makeNodesHiddenOnListItemsUIAndClearAnswers(
                    navigationItem.mandatoryNodes
                  );
                }
              });
            }
          });
        }
        // hide all the mandatoryNodes for the old answer navigationItem
        this.makeNodesHiddenOnListItemsUIAndClearAnswers(
          invalidConditionMandatoryNodes
        );
      }
    }
  }

  onDropdownAnswerModification(currentlyPreviewedQuestionAnswer) {
    // if this.currentlyPreviewedQuestion is doprdown type
    //. get the navigation[] for the curr preview qn
    const currPreviewNavigation =
      this.currentlyPreviewedQuestion.nodeLogic.navigate;
    //. get the curr answer navigationItem
    const currPreviewAnswerValidCondition = currPreviewNavigation?.find(
      (navigationItem) =>
        currentlyPreviewedQuestionAnswer.answerValue
          ?.map((x) => x.toLowerCase())
          .includes(navigationItem.condition.toLowerCase())
    );
    const validConditionMandatoryNodes =
      currPreviewAnswerValidCondition?.mandatoryNodes;
    //. show all the mandatoryNodes for the new answer navigationItem
    this.makeNodesRenderOnListItemsUI(validConditionMandatoryNodes);
    //. get the navigationItems for old anser (if not rest of the answers)
    const currPreviewAnswerInvalidConditions = currPreviewNavigation?.filter(
      (navigationItem) =>
        !currentlyPreviewedQuestionAnswer?.answerValue
          ?.map((x) => x.toLowerCase())
          ?.includes(navigationItem.condition.toLowerCase())
    );

    currPreviewAnswerInvalidConditions.forEach(
      (currPreviewAnswerInvalidCondition) => {
        const invalidConditionMandatoryNodes =
          currPreviewAnswerInvalidCondition?.mandatoryNodes;

        // check if any of the invalidConditionMandatoryNodes ['QN3'] is answred
        if (!this.areAnswersMappedIntoUIBySetAction) {
          this.handleSubNodesFor(invalidConditionMandatoryNodes);
        }
      }
    );
    //. Finally for all the answered questions show their respective conditional mandatoryNodes
    this.showRespectiveMandatoryNodesForAllTheQuestionsAnswered();
  }

  onDropdownAnswerReset(currentlyPreviewedQuestionAnswer) {
    //. get the navigation[] for the curr preview qn
    const currPreviewNavigation =
      this.currentlyPreviewedQuestion.nodeLogic.navigate;
    //. for each navigationItem get the mandatoryNodes
    currPreviewNavigation.forEach((navigationItem) =>
      this.handleSubNodesFor(navigationItem.mandatoryNodes)
    );
  }

  onCheckboxAnswerModification(currentlyPreviewedQuestionAnswer) {
    // if this.currentlyPreviewedQuestion is checkbox type
    //. get the navigation[] for the curr preview qn
    const currPreviewNavigation =
      this.currentlyPreviewedQuestion.nodeLogic.navigate;
    //. get the curr answer navigationItem
    const currPreviewAnswerValidConditions = currPreviewNavigation?.filter(
      (navigationItem) =>
        currentlyPreviewedQuestionAnswer.answerValue
          ?.map((x) => x?.toLowerCase())
          ?.includes(navigationItem.condition?.toLowerCase())
    );
    currPreviewAnswerValidConditions?.forEach(
      (currPreviewAnswerValidCondition) => {
        const validConditionMandatoryNodes =
          currPreviewAnswerValidCondition?.mandatoryNodes;
        //. show all the mandatoryNodes for the new answer navigationItem
        this.makeNodesRenderOnListItemsUI(validConditionMandatoryNodes);
      }
    );
    //. get the curr answer navigationItem
    const currPreviewAnswerInvalidConditions = currPreviewNavigation?.filter(
      (navigationItem) =>
        !currentlyPreviewedQuestionAnswer.answerValue
          ?.map((x) => x?.toLowerCase())
          ?.includes(navigationItem.condition?.toLowerCase())
    );

    currPreviewAnswerInvalidConditions?.forEach(
      (currPreviewAnswerInvalidCondition) => {
        const invalidConditionMandatoryNodes =
          currPreviewAnswerInvalidCondition?.mandatoryNodes;

        // check if any of the invalidConditionMandatoryNodes ['QN3'] is answred
        if (!this.areAnswersMappedIntoUIBySetAction) {
          this.handleSubNodesFor(invalidConditionMandatoryNodes);
        }
      }
    );
    //. Finally for all the answered questions show their respective conditional mandatoryNodes
    this.showRespectiveMandatoryNodesForAllTheQuestionsAnswered();
  }

  // This method is same as onAnswerReste() - @TODO: DRY
  onCheckboxAnswerReset() {
    //. get the navigation[] for the curr preview qn
    const currPreviewNavigation =
      this.currentlyPreviewedQuestion.nodeLogic.navigate;
    //. for each navigationItem get the mandatoryNodes
    currPreviewNavigation?.forEach((navigationItem) =>
      this.handleSubNodesFor(navigationItem.mandatoryNodes)
    );
  }

  onAnswerUpdate(
    currentlyPreviewedQuestionAnswer: PreviewQuestionAnswerDetailsSaved
  ) {
    if (
      this.currentlyPreviewedQuestion.data[1].type.toLowerCase() === 'dropdown'
    ) {
      //. if answer modifed
      if (currentlyPreviewedQuestionAnswer.answerValue) {
        this.onDropdownAnswerModification(currentlyPreviewedQuestionAnswer);
      }
      // if answer reset
      else {
        this.onDropdownAnswerReset(currentlyPreviewedQuestionAnswer);
      }
    } else if (
      this.currentlyPreviewedQuestion.data[1].type.toLowerCase() === 'checkbox'
    ) {
      if (currentlyPreviewedQuestionAnswer.answerValue) {
        if (currentlyPreviewedQuestionAnswer.answerValue?.length === 0) {
          this.currentlyPreviewedQuestion.answered = false;
          this.onCheckboxAnswerReset();
        } else {
          this.onCheckboxAnswerModification(currentlyPreviewedQuestionAnswer);
        }
      } else {
        this.onCheckboxAnswerReset();
      }
    } else if (
      this.currentlyPreviewedQuestion.data[1].type.toLowerCase() === 'radio'
    ) {
      if (currentlyPreviewedQuestionAnswer.answerValue) {
        this.onDropdownAnswerModification(currentlyPreviewedQuestionAnswer);
      }
    }
  }

  inquireNextNode(): RenderPreview {
    let nextQues: RenderPreview = new RenderPreview();
    const questionsRenderedOnListItemsUI = [...this.renderPreviewData]?.filter(
      (question) => question?.shallRenderOnListItemsUI
    );
    const currQuesIdx = [...questionsRenderedOnListItemsUI]?.findIndex(
      (qn) =>
        qn?.data[0]?.QuestionId ===
        this.currentlyPreviewedQuestion?.data[0]?.QuestionId
    );
    const nextQuesIdx = currQuesIdx + 1;
    nextQues = questionsRenderedOnListItemsUI?.[nextQuesIdx];
    return nextQues;
  }

  inquirePreviousNode(): RenderPreview {
    let prevQues: RenderPreview = new RenderPreview();
    const questionsRenderedOnListItemsUI = [...this.renderPreviewData]?.filter(
      (question) => question?.shallRenderOnListItemsUI
    );
    const currQuesIdx = [...questionsRenderedOnListItemsUI]?.findIndex(
      (qn) =>
        qn?.data[0]?.QuestionId ===
        this.currentlyPreviewedQuestion?.data[0]?.QuestionId
    );
    const prevQuesIdx = currQuesIdx - 1;
    prevQues = questionsRenderedOnListItemsUI?.[prevQuesIdx];
    return prevQues;
  }

  goToNextNode(qnPreviewedCurrently: RenderPreview) {

    const nextQues: RenderPreview = this.inquireNextNode();
    this.preview(nextQues);
  }

  goToPreviousNode() {
    const prevQues: RenderPreview = this.inquirePreviousNode();
    this.preview(prevQues);
  }

  getScores() {
    const formData: FormDataPayload[] = [...this.renderPreviewData].map(
      (qnObj) => ({
        question: qnObj?.data[0]?.QuestionId ?? '',
        answers: qnObj?.nodeLogic?.selectedAnswers ?? [''],
      })
    );
    // Send as single object with formData and formType properties
    // Backend expects ScoresByJson object, not an array
    // Include surveyId for CMMC forms to get proper CMMC scores with Risk section
    const getScoresForThePayload = {
      formData: formData,
      formType: this.formType,
      surveyId: this.surveyId,
    };
    this._buyerPreviewService
      .getCQScoresByJSON(getScoresForThePayload)
      .subscribe({
        next: (resp) => {
          if (resp.success) {
            this._buyerPreviewService.cqScore$ = resp.data[0];
          } else {
            this.swalWithBootstrapButtons.fire({
              title: 'Failure!',
              text:
                !resp.success || resp.msg != ''
                  ? resp.msg
                  : 'Failed to fetch scores.',
              icon: 'error',
              confirmButtonText: 'OK',
            });
          }
        },
        error: (err) => {
          // this.loaderService.displayLoader = false;
          this.swalWithBootstrapButtons.fire({
            title: 'Failure!',
            text:
              !err.success || err.msg != ''
                ? err.msg
                : 'Failed to fetch scores.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        },
      });
  }

  validate() {}

  submitConfirm() {
    this._buyerPreviewService.helper$.next('SUBMIT_CONFIRMATION_MODAL_OPEN');
  }

  submit() {
    const userAnswers = {
      formId: this.orgFormId, //intentional change as API seeks this value
      SurveyUniqueID: this.surveyId,
      OrgFormUniqueId: this.orgFormId,
    };
    this._buyerPreviewService.submitUserAnswers(userAnswers).subscribe({
      next: (resp) => {
        this.swalWithBootstrapButtons
          .fire({
            title: resp.IsSuccess ? 'Success!' : 'Failure!',
            text:
              resp.IsSuccess && resp.Message == ''
                ? 'Form submitted successfully'
                : resp.Message,
            icon: resp.IsSuccess ? 'success' : 'error',
            confirmButtonText: 'OK',
          })
          .then((result) => {
            if (result.isConfirmed) {
              const redirectUrl = this.submitFormRedirectURL;
              window.parent.location.href = redirectUrl;
              this.loaderService.showLoader();
            }
          });
      },
      error: (err) => {
        this.swalWithBootstrapButtons.fire({
          title: 'Failure!',
          text:
            !err.Success || err.Msg != ''
              ? err.Msg
              : 'Failed to submit the form.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        console.log("Buyer preview submit failed");
      },
    });
  }

  areAllNonStaticNonSubmitterQuestionsAnsweredAndValid() {
    return [...this.renderPreviewData]
      ?.filter(
        (qnObj) =>
          !this.doesHiddenInputExistFor(qnObj) &&
          !this.doesDisableInputExistFor(qnObj) &&
          qnObj?.data[0]?.type?.toLowerCase() !== 'submitter' &&
          qnObj.shallRenderOnListItemsUI
      )
      ?.every((qnObj) => qnObj.answered && qnObj.valid);
  }

  areAllSubmitterQuestionsAnswered() {
    return [...this.renderPreviewData]
      ?.filter((qnObj) => qnObj?.data[0]?.type?.toLowerCase() === 'submitter')
      ?.every((qnObj) => qnObj.answered);
  }

  save(triggeredFrom?: string) {
    const userAnswers = {
      id: this.orgFormId,
      jsonResponse: [...this.renderPreviewData].map((qnObj) => ({
        id: qnObj.data[0].QuestionId,
        question: qnObj?.data[0]?.dispalyFCText ?? '',
        answers: qnObj?.nodeLogic?.selectedAnswers?.join('|').toString() ?? '',
      })),
      FormLayout: this.formLayout,
    };

    this._buyerPreviewService.saveUserAnswers(userAnswers).subscribe({
      next: (resp) => {
        this.enableSubmitButton =
          this.areAllNonStaticNonSubmitterQuestionsAnsweredAndValid() &&
          this.areAllSubmitterQuestionsAnswered() &&
          resp.IsSuccess;
        
        if (!triggeredFrom) {
          this.swalWithBootstrapButtons.fire({
            title: resp.IsSuccess ? 'Success!' : 'Failure!',
            text:
              resp.IsSuccess && resp.Message == ''
                ? 'Form saved successfully'
                : resp.Message,
            icon: resp.IsSuccess ? 'success' : 'error',
            confirmButtonText: 'OK',
          });
        }
      },
      error: (err) => {
        if (!triggeredFrom) {
          this.swalWithBootstrapButtons.fire({
            title: 'Failure!',
            text:
              !err.Success || err.Msg != ''
                ? err.Msg
                : 'Failed to save the form.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      },
    });
  }

  exit() {
    if (
      window.confirm('Any unsaved data will be lost. Do you want to continue?')
    ) {
      this.loaderService.showLoader();
      window.parent.location.href = this.submitFormRedirectURL;
    }
  }

  clearAnswersForAllTheQuestions() {
    this.renderPreviewData.forEach((item) => {
      this._handleAnswerService.clearAnswersFor(item);
    });
  }

  ngOnDestroy() {
    this._placeholderSub?.unsubscribe();
    this.clearAnswersForAllTheQuestions();
    localStorage.removeItem('RENDER_PREVIEW_QUESTIONS_DATA');
  }
}
